import axios, { type AxiosInstance } from 'axios';
import { AUTH_REQUEST_TIMEOUT_MS } from './config';
import { csrfHeaders } from './csrf';
import type { DeviceIdStore } from './deviceId';
import { safeGet, safeRemove, safeSet, type AuthEnvironment } from './environment';
import { decodeUserId } from './jwt';
import type { AuthNamespace } from './namespace';
import type { IAuthResponse } from './signIn';
import type { TokenStore } from './tokenStore';

/** `multiAccount` из `GET /Account/SignInSettings`; `null` — мультиаккаунт на стенде выключен. */
export interface MultiAccountSettings {
    enabled: boolean;
    /** Предел учётных записей в связке вместе с активной. */
    maxAccounts: number;
}

/** Идентификатор пользователя — как его отдал сервер: в запросы уходит без преобразования. */
export type AccountUserId = number | string;

/** Запаркованная учётная запись из `GET /Account/Accounts` (текущего пользователя в списке нет). */
export interface ParkedAccount {
    userId: AccountUserId;
    login: string;
    displayName: string;
    /** Refresh отозван или истёк: переключиться нельзя, нужен вход заново. */
    expired: boolean;
}

/** `error` тела отказа эндпоинтов `Account/Accounts…` и их фильтров (CSRF, заголовок связки, вход от имени). */
export type AccountsErrorCode =
    | 'limit'
    | 'no_session'
    | 'account_not_found'
    | 'token_invalid'
    | 'token_reuse'
    | 'busy'
    | 'access_denied'
    | 'support_session'
    | 'csrf_mismatch'
    | 'header_required'
    | 'temporarily_unavailable';

/**
 * Заголовок всех запросов связки: без него сервер отвечает 400. Нестандартный заголовок заставляет браузер делать
 * CORS-preflight — защита от CSRF, пока активной сессии (и её CSRF-cookie) нет.
 */
export const ACCOUNTS_HEADER = 'X-Srm-Accounts';

/** Параметр страницы входа: `add=1` — добавить учётную запись к связке браузера. */
export const ADD_ACCOUNT_PARAM = 'add';

/**
 * Мультиаккаунт недоступен: выключен в настройках входа, вкладка изолированного входа («от имени») или браузер без
 * Web Locks — без них переключение не встанет в очередь с тихой ротацией соседних вкладок.
 */
export class AccountsUnavailableError extends Error {
    readonly reason: 'disabled' | 'isolated' | 'unsupported';

    constructor(reason: 'disabled' | 'isolated' | 'unsupported') {
        super(
            reason === 'isolated'
                ? 'Переключение учётных записей недоступно во вкладке входа от имени пользователя'
                : reason === 'unsupported'
                  ? 'Браузер не поддерживает переключение учётных записей'
                  : 'Переключение учётных записей выключено',
        );
        this.name = 'AccountsUnavailableError';
        this.reason = reason;
    }
}

/** Пока шёл запрос переключения, сессию сняли (выход здесь или в соседней вкладке): результат не принят. */
export class AccountsSessionChangedError extends Error {
    constructor() {
        super('Сессия завершилась во время переключения учётной записи');
        this.name = 'AccountsSessionChangedError';
    }
}

const ERROR_CODES: readonly AccountsErrorCode[] = [
    'limit',
    'no_session',
    'account_not_found',
    'token_invalid',
    'token_reuse',
    'busy',
    'access_denied',
    'support_session',
    'csrf_mismatch',
    'header_required',
    'temporarily_unavailable',
];

export function accountsErrorCode(error: unknown): AccountsErrorCode | undefined {
    if (!axios.isAxiosError(error)) return undefined;
    const code = (error.response?.data as { error?: unknown } | undefined)?.error;
    return ERROR_CODES.find((known) => known === code);
}

/** Отказ связки для интерфейса: `message` нет — код сервера неизвестен, текст с кодом ответа собирает вызывающий. */
export interface AccountsFailure {
    code?: AccountsErrorCode;
    message?: string;
}

export interface AccountsFailureTexts {
    /** Предел из настроек входа — в тексте отказа `limit`. */
    maxAccounts?: number;
    /** Своё `access_denied` приложения (у админки — права на панель управления). */
    accessDenied?: string;
}

/** Текст отказа связки для меню и страницы входа. */
export function describeAccountsFailure(error: unknown, texts: AccountsFailureTexts = {}): AccountsFailure {
    if (error instanceof AccountsUnavailableError || error instanceof AccountsSessionChangedError) {
        return { message: error.message };
    }
    const code = accountsErrorCode(error);
    const message = (() => {
        switch (code) {
            case 'limit':
                return texts.maxAccounts
                    ? `Можно добавить не больше ${texts.maxAccounts} учётных записей — уберите одну из списка`
                    : 'Достигнут предел учётных записей — уберите одну из списка';
            case 'no_session':
                return 'Текущая сессия уже завершена — войдите заново';
            case 'account_not_found':
                return 'Учётной записи больше нет в списке';
            case 'token_invalid':
                return 'Вход в учётную запись истёк — войдите заново';
            case 'token_reuse':
                return 'Вход в учётную запись отозван — войдите заново';
            case 'busy':
                return 'Учётная запись уже переключается — повторите через несколько секунд';
            case 'access_denied':
                return texts.accessDenied ?? 'У учётной записи нет доступа к приложению';
            case 'support_session':
                return 'Во время входа от имени пользователя переключать учётные записи нельзя';
            case 'csrf_mismatch':
            case 'header_required':
                return 'Сессия изменилась в другой вкладке — обновите страницу';
            case 'temporarily_unavailable':
                return 'Сервис временно недоступен — повторите позже';
            default:
                return undefined;
        }
    })();
    return { code, message };
}

/**
 * Query страницы входа «Добавить учётную запись»: `add=1` и `returnUrl` — куда вернуться после входа или «Отмены»
 * (путь от basename). Путь страницы входа и basename — у приложения.
 */
export function addAccountSearch(returnUrl?: string): string {
    const params = new URLSearchParams({ [ADD_ACCOUNT_PARAM]: '1' });
    if (returnUrl && returnUrl !== '/') params.set('returnUrl', returnUrl);
    return params.toString();
}

/** sessionStorage: учётная запись, которую эта вкладка запарковала перед «Добавить», — «Отмена» на странице входа. */
export const parkedFromKey = (namespace: Pick<AuthNamespace, 'broadcastChannel'>): string =>
    `${namespace.broadcastChannel}__parked-from__`;

/** sessionStorage: логин протухшей учётной записи для формы входа — не в адресе (журналы nginx, история, Referer). */
export const loginHintKey = (namespace: Pick<AuthNamespace, 'broadcastChannel'>): string =>
    `${namespace.broadcastChannel}__login-hint__`;

export interface AuthAccounts {
    /**
     * Настройки из `SignInSettings.multiAccount` — приложение передаёт их там же, где режим (`setMode`). До вызова и
     * с `null` мультиаккаунт выключен.
     */
    configure(settings: MultiAccountSettings | null | undefined): void;
    /** Включён на стенде, вкладка не изолированного входа и в браузере есть Web Locks. Скрытие на PROD — у приложения. */
    enabled(): boolean;
    /** Запаркованные учётные записи без текущей (сервер узнаёт её по Bearer). */
    list(): Promise<ParkedAccount[]>;
    /**
     * Сделать учётную запись активной: под Web Lock ротации сервер ротирует её refresh и паркует текущую; ответ
     * принимается как вход (`acceptLogin`) — соседние вкладки получают токен другого пользователя (R9, `onUserSwitch`).
     * Незавершённый выход сначала доводится до отзыва; не удалось — отказ. Отказ — отказ промиса, текущая сессия не
     * меняется. Перезагрузку страницы делает приложение.
     */
    switchTo(userId: AccountUserId): Promise<void>;
    /**
     * Запарковать активную учётную запись (перед входом ещё одной): под Web Lock ротации; после ответа токены вкладки
     * забываются (`clearTokens`, сдвиг поколения сессии) без отметки незавершённого выхода — семейство живо в связке;
     * соседним вкладкам — `logged-out`. `loginHint` — логин для формы входа (вход заново в протухшую).
     */
    park(loginHint?: string): Promise<void>;
    /** Убрать учётную запись из связки с отзывом её семейства (под Web Lock); без `userId` — все запаркованные. */
    remove(userId?: AccountUserId): Promise<void>;
    /** Учётная запись (claim пользователя access), запаркованная этой вкладкой под «Добавить»; снимает её любой вход. */
    parkedFrom(): string | null;
    /** Логин, переданный `park(loginHint)` на страницу входа этой вкладки; снимает его любой вход. */
    loginHint(): string | null;
}

export interface AccountsOptions {
    apiUrl: string;
    withCredentials: boolean;
    namespace: Pick<AuthNamespace, 'csrfCookie' | 'csrfHeader' | 'broadcastChannel'>;
    transport?: Pick<AxiosInstance, 'post' | 'get'>;
    env: Pick<AuthEnvironment, 'cookies' | 'sessionStorage' | 'locks'>;
    tokens: TokenStore;
    deviceId: Pick<DeviceIdStore, 'get'>;
    isIsolated: () => boolean;
    /** Web Lock ротации namespace: переключение, парковка и удаление не должны столкнуться с тихой ротацией. */
    withRefreshLock: <T>(task: () => Promise<T>) => Promise<T>;
    acceptLogin: (response: Pick<IAuthResponse<unknown>, 'accessToken' | 'refreshToken' | 'authMode'>) => void;
    /** Отзыв семейства по access (SignOut), когда результат переключения пережил бы выход. */
    revoke: (accessToken: string) => Promise<unknown>;
    /** Отметка незавершённого выхода (cookie-режим): refresh-cookie выходившей учётной записи ещё жива. */
    pendingSignOut: () => boolean;
    /** Довести незавершённый выход до отзыва семейства (берёт Web Lock сам). */
    revokePendingSignOut: () => Promise<void>;
}

/** Связка учётных записей браузера (запаркованные лежат в зашифрованной httpOnly-cookie сервера). */
export function createAccounts({
    apiUrl,
    withCredentials,
    namespace,
    transport = axios,
    env,
    tokens,
    deviceId,
    isIsolated,
    withRefreshLock,
    acceptLogin,
    revoke,
    pendingSignOut,
    revokePendingSignOut,
}: AccountsOptions): AuthAccounts {
    let configured = false;
    const returnKey = parkedFromKey(namespace);
    const hintKey = loginHintKey(namespace);
    const url = (path: string) => `${apiUrl}/Account/${path}`;

    const unavailable = (): AccountsUnavailableError | null => {
        if (isIsolated()) return new AccountsUnavailableError('isolated');
        if (!configured) return new AccountsUnavailableError('disabled');
        return env.locks ? null : new AccountsUnavailableError('unsupported');
    };

    /**
     * Как у RefreshToken: CSRF из cookie namespace (нет cookie — без заголовка), cookie связки — `withCredentials`.
     * Bearer — чтобы сервер закрыл сессию уходящей учётной записи и знал, кто переключается.
     */
    const requestConfig = () => {
        const accessToken = tokens.getAccessToken();
        return {
            withCredentials,
            headers: {
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                [ACCOUNTS_HEADER]: '1',
                ...csrfHeaders(namespace, env.cookies()),
            },
            timeout: AUTH_REQUEST_TIMEOUT_MS,
        };
    };

    const post = <T>(path: string, body: unknown) =>
        transport.post<T>(url(path), body, requestConfig()).then((response) => response.data);

    /** Тело с активной сессией: в body-режиме refresh из хранилища, в cookie-режиме его приложит браузер. */
    const sessionBody = () => {
        const refreshToken = tokens.getMode() === 'body' ? tokens.getRefreshToken() : null;
        return { deviceId: deviceId.get(), ...(refreshToken ? { refreshToken } : {}) };
    };

    /**
     * Refresh-cookie выходившей учётной записи браузер приложил бы к запросу, и сервер запарковал бы её живой: сначала
     * отзыв, потом — под замком — повторная проверка (выход в соседней вкладке мог начаться, пока ждали).
     */
    const settlePendingSignOut = async () => {
        if (pendingSignOut()) await revokePendingSignOut();
        if (pendingSignOut()) throw new AccountsSessionChangedError();
    };

    const exclusive = <T>(task: () => Promise<T>) =>
        withRefreshLock(async () => {
            if (pendingSignOut()) throw new AccountsSessionChangedError();
            return task();
        });

    const guarded =
        <A extends unknown[], R>(task: (...args: A) => Promise<R>) =>
        (...args: A): Promise<R> => {
            const error = unavailable();
            return error ? Promise.reject(error) : task(...args);
        };

    return {
        configure(next) {
            configured = !!next?.enabled;
        },

        enabled: () => !unavailable(),

        list: guarded(async () => {
            const response = await transport.get<{ accounts?: ParkedAccount[] }>(url('Accounts'), requestConfig());
            const accounts = response.data?.accounts;
            return Array.isArray(accounts) ? accounts : [];
        }),

        switchTo: guarded(async (userId: AccountUserId) => {
            await settlePendingSignOut();
            await exclusive(async () => {
                const epoch = tokens.epoch();
                const response = await post<IAuthResponse<unknown>>('Accounts/Switch', {
                    userId,
                    authMode: tokens.getMode(),
                    ...sessionBody(),
                });
                if (tokens.epoch() !== epoch) {
                    // Выход случился во время запроса: новое семейство не должно его пережить.
                    await revoke(response.accessToken).catch(() => undefined);
                    throw new AccountsSessionChangedError();
                }
                acceptLogin(response);
            });
        }),

        park: guarded(async (loginHint?: string) => {
            await settlePendingSignOut();
            await exclusive(async () => {
                const accessToken = tokens.getAccessToken();
                const userId = accessToken ? decodeUserId(accessToken) : undefined;
                await post<void>('Accounts/Park', sessionBody());
                if (userId) safeSet(env.sessionStorage, returnKey, userId);
                else safeRemove(env.sessionStorage, returnKey);
                if (loginHint) safeSet(env.sessionStorage, hintKey, loginHint);
                else safeRemove(env.sessionStorage, hintKey);
                tokens.clearTokens('logout');
            });
        }),

        remove: guarded((userId?: AccountUserId) =>
            withRefreshLock(async () => {
                await post<void>('Accounts/Remove', userId === undefined ? {} : { userId });
                if (userId === undefined || String(userId) === safeGet(env.sessionStorage, returnKey)) {
                    safeRemove(env.sessionStorage, returnKey);
                }
            }),
        ),

        parkedFrom: () => (unavailable() ? null : safeGet(env.sessionStorage, returnKey)),

        loginHint: () => (unavailable() ? null : safeGet(env.sessionStorage, hintKey)),
    };
}
